import React, { FC, ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Icon } from '@/app/common/Icon';
import { Typography } from '@/app/common/Typography';
import { SocialLinks } from '@/app/common/Links/SocialLinks';
import { getDirectImageUrl, getImageBuilder } from '@/app/lib/media-helper';
import { cn } from '@/app/lib/utils';
import type { FooterSocialLink } from '../interfaces';
import type { ProcessedContactInfo } from '../lib/footer.utils';
import { footerVariants } from '@workspace/widgets/Footer/footer.variants';

export interface FooterContactProps {
  contactInfo?: ProcessedContactInfo;
  socialLinks: FooterSocialLink[];
  footerSocialLinks?: FooterSocialLink[];
  className?: string;
}

const ContactIcon: FC<{
  icon?: string;
  iconImageUrl?: string;
  bynderIconImage?: any;
  size?: number;
}> = ({ icon, iconImageUrl, bynderIconImage, size = 24 }) => {
  const { contactIcon } = footerVariants();
  const iconColor = contactIcon?.() || 'text-black';
  const resolvedIconUrl =
    iconImageUrl ||
    getDirectImageUrl(bynderIconImage) ||
    (bynderIconImage ? getImageBuilder(bynderIconImage).width(size).height(size).fit('max').url() : undefined);

  if (resolvedIconUrl) {
    return (
      <Image
        src={resolvedIconUrl}
        alt="contact icon"
        width={size}
        height={size}
        className={cn('object-contain', iconColor)}
      />
    );
  }
  return icon ? <Icon name={icon as any} size={size} className={iconColor} /> : null;
};

const ContactTextItem: FC<{
  href?: string;
  icon?: string;
  iconImageUrl?: string;
  bynderIconImage?: any;
  children: ReactNode;
  className?: string;
  isHeading?: boolean;
  isOpenInNewTab?: boolean;
}> = ({
  href,
  icon,
  iconImageUrl,
  bynderIconImage,
  children,
  className = '',
  isHeading = false,
  isOpenInNewTab = false,
}) => {
  const hasValidHref = Boolean(href && href !== 'null' && href.trim() !== '');
  const { contactHeading, contactDetail } = footerVariants();
  const itemStyle = isHeading ? contactHeading?.() : contactDetail?.();
  const textClasses = cn('flex items-center gap-xs', itemStyle, className);

  const content = (
    <>
      <ContactIcon icon={icon} iconImageUrl={iconImageUrl} bynderIconImage={bynderIconImage} />
      {children}
    </>
  );

  if (!hasValidHref || !href) {
    return <div className={textClasses}>{content}</div>;
  }

  return (
    <Link
      href={href}
      target={isOpenInNewTab ? '_blank' : undefined}
      rel={isOpenInNewTab ? 'noopener noreferrer' : undefined}
      className="inline-flex"
    >
      <Typography variant="link" className={cn(textClasses, '!no-underline')}>
        {content}
      </Typography>
    </Link>
  );
};

const isContactDetail = (item?: ProcessedContactInfo['contactItems'][number]) => {
  if (!item) return false;
  const url = (item.url || '').trim().toLowerCase();
  const text = (item.text || '').trim().toLowerCase();
  return (
    url.startsWith('tel:') ||
    url.startsWith('mailto:') ||
    text.startsWith('phone') ||
    text.startsWith('email') ||
    /@/.test(text)
  );
};

const isItemHeading = (item?: ProcessedContactInfo['contactItems'][number]) => {
  if (!item) return false;
  if (item.isBold) return true;
  return !isContactDetail(item);
};

const ContactItemsRenderer: FC<{
  items: ProcessedContactInfo['contactItems'];
}> = ({ items }) => {
  const getGapClass = (currentIsHeading?: boolean, nextIsHeading?: boolean) => {
    if (currentIsHeading && nextIsHeading) return 'h-3';
    if (currentIsHeading !== nextIsHeading) return 'h-4';
    return 'h-2';
  };

  return (
    <div className="flex flex-col">
      {items.map((item: ProcessedContactInfo['contactItems'][number], index: number) => {
        const isLast = index === items.length - 1;
        const next = items[index + 1];
        const isHeading = isItemHeading(item);
        const nextIsHeading = isItemHeading(next);

        return (
          <React.Fragment key={index}>
            <ContactTextItem
              href={item.url || undefined}
              icon={item.icon}
              iconImageUrl={item.iconImageUrl}
              bynderIconImage={item.bynderIconImage}
              isHeading={isHeading}
              isOpenInNewTab={item.isOpenInNewTab}
            >
              {item.text}
            </ContactTextItem>

            {!isLast && <div className={getGapClass(isHeading, nextIsHeading)} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const FooterContact: FC<FooterContactProps> = ({
  contactInfo,
  socialLinks,
  footerSocialLinks,
  className = '',
}) => {
  const contactItems = contactInfo?.contactItems || [];

  return (
    <div className={cn('md:col-span-1 space-y-md justify-end', className)}>
      {contactItems.length > 0 && <ContactItemsRenderer items={contactItems} />}
      <div className="flex justify-start">
        <SocialLinks
          links={(footerSocialLinks || socialLinks || []).map((s) => ({
            platform: s?.platform,
            url: s?.url ?? s?.linkOptional?.url ?? '#',
            isOpenInNewTab: s?.isOpenInNewTab,
            linkType: s?.linkType ?? s?.linkOptional?.type,
            _ref: s?._ref,
            _type: s?._type,
            link: s?.linkOptional,
          }))}
          className="gap-5 md:gap-sm"
          spacingMode="gap"
        />
      </div>
    </div>
  );
};
